import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, CreditCard, Wallet, Calendar, Clock, ShoppingBag, AlertCircle, Truck, Store, MapPin, Landmark } from "lucide-react";
import axios from "axios";
import { useCart, API } from "@/App";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";

const POSTCODE_REGEX = /^M\d{1,2}/i;

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cancelled = searchParams.get("cancelled") === "true";

  const { cart, cartTotal, clearCart, settings } = useCart();
  const [loading, setLoading] = useState(false);
  const [timeSlots, setTimeSlots] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);

  // Payment method availability from admin settings (fallback: all enabled)
  const stripeEnabled = settings?.stripe_enabled ?? true;
  const bankTransferEnabled = settings?.bank_transfer_enabled ?? true;
  const payAtPickupEnabled = settings?.pay_at_pickup_enabled ?? true;
  const bankAccountName = settings?.bank_account_name || "C O International LTD";
  const bankSortCode = settings?.bank_sort_code || "23-11-85";
  const bankAccountNumber = settings?.bank_account_number || "00165856";

  // Delivery config (loaded from backend)
  const [deliveryConfig, setDeliveryConfig] = useState({
    dates: [],
    time_windows: [],
    min_order: 50,
    area: "Manchester (M postcodes only)",
  });
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [deliveryQuoteMsg, setDeliveryQuoteMsg] = useState("");

  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    fulfillment_type: "collection", // "collection" | "delivery"
    pickup_date: "",
    pickup_time: "",
    delivery_address_line1: "",
    delivery_address_line2: "",
    delivery_city: "Manchester",
    delivery_postcode: "",
    payment_method: "stripe",
    notes: "",
  });

  // Fetch collection time slots + delivery config
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [slotsRes, deliveryRes] = await Promise.all([
          axios.get(`${API}/timeslots`),
          axios.get(`${API}/delivery/saturdays`),
        ]);
        setTimeSlots(slotsRes.data);
        const dates = [...new Set(slotsRes.data.map((s) => s.date))].sort();
        setAvailableDates(dates);
        setDeliveryConfig(deliveryRes.data);
      } catch (error) {
        console.error("[Checkout] Failed to load timeslots/delivery config:", error);
      }
    };
    fetchAll();
  }, []);

  // Reset date/time when fulfillment changes; force Stripe or bank_transfer for delivery
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      pickup_date: "",
      pickup_time: "",
      payment_method:
        prev.fulfillment_type === "delivery" && prev.payment_method === "pay_at_pickup"
          ? "stripe"
          : prev.payment_method,
    }));
  }, [formData.fulfillment_type]);

  // If the currently-selected payment method is disabled by admin settings,
  // switch to the first available one so the customer isn't stuck.
  useEffect(() => {
    const availability = {
      stripe: stripeEnabled,
      bank_transfer: bankTransferEnabled,
      pay_at_pickup: payAtPickupEnabled && formData.fulfillment_type !== "delivery",
    };
    if (!availability[formData.payment_method]) {
      const firstEnabled = Object.entries(availability).find(([, on]) => on)?.[0];
      if (firstEnabled) {
        setFormData((prev) => ({ ...prev, payment_method: firstEnabled }));
      }
    }
  }, [stripeEnabled, bankTransferEnabled, payAtPickupEnabled, formData.payment_method, formData.fulfillment_type]);

  // Compute delivery fee whenever postcode changes
  useEffect(() => {
    const pc = formData.delivery_postcode.trim();
    if (formData.fulfillment_type !== "delivery" || !pc) {
      setDeliveryFee(0);
      setDeliveryQuoteMsg("");
      return;
    }
    if (!POSTCODE_REGEX.test(pc)) {
      setDeliveryFee(0);
      setDeliveryQuoteMsg("We only deliver to Manchester (M) postcodes.");
      return;
    }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/delivery/quote`, {
          params: { postcode: pc },
          signal: ctrl.signal,
        });
        if (res.data.deliverable) {
          setDeliveryFee(res.data.fee);
          setDeliveryQuoteMsg(`Delivery fee: £${res.data.fee.toFixed(2)}`);
        } else {
          setDeliveryFee(0);
          setDeliveryQuoteMsg(res.data.message);
        }
      } catch (e) {
        if (e.name !== "CanceledError" && e.name !== "AbortError") {
          console.error("[Checkout] Failed to get delivery quote:", e);
        }
      }
    }, 350);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [formData.delivery_postcode, formData.fulfillment_type]);

  const isDelivery = formData.fulfillment_type === "delivery";

  const collectionTimes = timeSlots
    .filter((s) => s.date === formData.pickup_date && s.current_orders < s.max_orders)
    .map((s) => s.time)
    .sort();

  const dateOptions = isDelivery ? deliveryConfig.dates : availableDates;
  const timeOptions = isDelivery ? deliveryConfig.time_windows : collectionTimes;

  const minOrderShortfall = isDelivery && cartTotal < deliveryConfig.min_order
    ? deliveryConfig.min_order - cartTotal
    : 0;

  const total = cartTotal + (isDelivery ? deliveryFee : 0);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === "pickup_date") {
      setFormData((prev) => ({ ...prev, pickup_time: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.customer_name || !formData.customer_email || !formData.customer_phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!formData.pickup_date || !formData.pickup_time) {
      toast.error(`Please select a ${isDelivery ? "delivery" : "pickup"} date and time`);
      return;
    }

    if (isDelivery) {
      if (!formData.delivery_address_line1 || !formData.delivery_postcode) {
        toast.error("Please enter your delivery address and postcode");
        return;
      }
      if (!POSTCODE_REGEX.test(formData.delivery_postcode.trim())) {
        toast.error("We only deliver to Manchester (M) postcodes");
        return;
      }
      if (cartTotal < deliveryConfig.min_order) {
        toast.error(`Minimum order for delivery is £${deliveryConfig.min_order}. Add £${minOrderShortfall.toFixed(2)} more.`);
        return;
      }
    }

    if (cart.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    setLoading(true);
    try {
      const orderData = {
        ...formData,
        items: cart,
        origin_url: window.location.origin,
      };
      const res = await axios.post(`${API}/orders`, orderData);

      if (formData.payment_method === "stripe" && res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        clearCart();
        navigate(`/order-confirmation?order_id=${res.data.order.id}`);
        toast.success("Order placed successfully!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to place order");
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0 && !cancelled) {
    return (
      <div className="min-h-screen bg-white" data-testid="checkout-page">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <ShoppingBag className="h-10 w-10 text-slate-400" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Your cart is empty</h1>
          <p className="text-slate-600 mb-8">Add some items before checkout</p>
          <Link to="/products">
            <Button className="bg-emerald-600 hover:bg-emerald-700">Continue Shopping</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="checkout-page">
      <Header />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/products" className="inline-flex items-center text-slate-600 hover:text-emerald-600 mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Continue Shopping
        </Link>

        {cancelled && (
          <Alert className="mb-6 border-amber-200 bg-amber-50">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              Payment was cancelled. You can try again or choose to pay at pickup.
            </AlertDescription>
          </Alert>
        )}

        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8">Checkout</h1>

        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              {/* Fulfillment Type */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">How would you like to receive your order?</h2>
                <RadioGroup
                  value={formData.fulfillment_type}
                  onValueChange={(val) => handleChange("fulfillment_type", val)}
                  className="grid sm:grid-cols-2 gap-3"
                >
                  <label
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                      !isDelivery ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid="fulfillment-collection"
                  >
                    <RadioGroupItem value="collection" id="ff-collection" />
                    <Store className="h-5 w-5 text-slate-600" />
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">Collection (Click & Collect)</p>
                      <p className="text-sm text-slate-500">Free • 7 days a week</p>
                    </div>
                  </label>
                  <label
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                      isDelivery ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid="fulfillment-delivery"
                  >
                    <RadioGroupItem value="delivery" id="ff-delivery" />
                    <Truck className="h-5 w-5 text-slate-600" />
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">Home Delivery</p>
                      <p className="text-sm text-slate-500">Saturdays only • Manchester (M postcodes)</p>
                    </div>
                  </label>
                </RadioGroup>
                {isDelivery && (
                  <div className="mt-4 space-y-2" data-testid="delivery-info">
                    <div className="text-xs text-slate-600 bg-slate-50 rounded-lg px-3 py-2">
                      Minimum order: <strong>£{deliveryConfig.min_order}</strong> · Zone fees: <strong>Zone 1 £7.00 · Zone 2 £7.50 · Zone 3 £8.00</strong>
                    </div>
                    {deliveryFee > 0 && (
                      <div className="flex items-center justify-between text-sm bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg px-3 py-2" data-testid="delivery-fee-banner">
                        <span>Delivery fee for <strong>{formData.delivery_postcode}</strong></span>
                        <span className="font-semibold">£{deliveryFee.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Contact Details */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Contact Details</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      value={formData.customer_name}
                      onChange={(e) => handleChange("customer_name", e.target.value)}
                      placeholder="John Smith"
                      required
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.customer_email}
                      onChange={(e) => handleChange("customer_email", e.target.value)}
                      placeholder="john@example.com"
                      required
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.customer_phone}
                      onChange={(e) => handleChange("customer_phone", e.target.value)}
                      placeholder="07123 456789"
                      required
                      data-testid="input-phone"
                    />
                  </div>
                </div>
              </div>

              {/* Delivery Address (only when delivery selected) */}
              {isDelivery && (
                <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="delivery-address-section">
                  <h2 className="text-lg font-semibold text-slate-900 mb-4">
                    <MapPin className="inline h-5 w-5 mr-2 text-emerald-600" />
                    Delivery Address
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <Label htmlFor="addr1">Address Line 1 *</Label>
                      <Input
                        id="addr1"
                        value={formData.delivery_address_line1}
                        onChange={(e) => handleChange("delivery_address_line1", e.target.value)}
                        placeholder="House number and street"
                        required={isDelivery}
                        data-testid="input-address1"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="addr2">Address Line 2 (Optional)</Label>
                      <Input
                        id="addr2"
                        value={formData.delivery_address_line2}
                        onChange={(e) => handleChange("delivery_address_line2", e.target.value)}
                        placeholder="Apartment, suite, unit, etc."
                        data-testid="input-address2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        value={formData.delivery_city}
                        onChange={(e) => handleChange("delivery_city", e.target.value)}
                        placeholder="Manchester"
                        required={isDelivery}
                        data-testid="input-city"
                      />
                    </div>
                    <div>
                      <Label htmlFor="postcode">Postcode * (M only)</Label>
                      <Input
                        id="postcode"
                        value={formData.delivery_postcode}
                        onChange={(e) => handleChange("delivery_postcode", e.target.value.toUpperCase())}
                        placeholder="e.g. M18 7EE"
                        required={isDelivery}
                        data-testid="input-postcode"
                      />
                      {deliveryQuoteMsg && (
                        <p className={`text-xs mt-1 ${deliveryFee > 0 ? "text-emerald-700" : "text-red-600"}`} data-testid="delivery-quote-msg">
                          {deliveryQuoteMsg}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Pickup / Delivery Time */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">
                  <Calendar className="inline h-5 w-5 mr-2 text-emerald-600" />
                  {isDelivery ? "Delivery Slot" : "Pickup Time"}
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>{isDelivery ? "Saturday" : "Pickup Date"} *</Label>
                    <Select value={formData.pickup_date} onValueChange={(val) => handleChange("pickup_date", val)}>
                      <SelectTrigger data-testid="select-date">
                        <SelectValue placeholder={isDelivery ? "Select Saturday" : "Select date"} />
                      </SelectTrigger>
                      <SelectContent>
                        {dateOptions.length === 0 ? (
                          <SelectItem value="none" disabled>
                            No dates available
                          </SelectItem>
                        ) : (
                          dateOptions.map((date) => (
                            <SelectItem key={date} value={date}>
                              {new Date(date).toLocaleDateString("en-GB", {
                                weekday: "short",
                                day: "numeric",
                                month: "short",
                              })}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{isDelivery ? "Time Window" : "Pickup Time"} *</Label>
                    <Select
                      value={formData.pickup_time}
                      onValueChange={(val) => handleChange("pickup_time", val)}
                      disabled={!formData.pickup_date}
                    >
                      <SelectTrigger data-testid="select-time">
                        <SelectValue placeholder={isDelivery ? "Select window" : "Select time"} />
                      </SelectTrigger>
                      <SelectContent>
                        {timeOptions.length === 0 ? (
                          <SelectItem value="none" disabled>
                            No slots available
                          </SelectItem>
                        ) : (
                          timeOptions.map((time) => (
                            <SelectItem key={time} value={time}>
                              {time}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-sm text-slate-500 mt-3">
                  <Clock className="inline h-4 w-4 mr-1" />
                  {isDelivery
                    ? "Delivery available Saturdays only between 9am – 6pm."
                    : "Pickup at: 578 Hyde Road, Manchester M18 7EE"}
                </p>
              </div>

              {/* Payment Method */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Payment Method</h2>
                <RadioGroup
                  value={formData.payment_method}
                  onValueChange={(val) => handleChange("payment_method", val)}
                  className="space-y-3"
                >
                  {stripeEnabled && (
                    <label
                      className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                        formData.payment_method === "stripe"
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid="payment-stripe"
                    >
                      <RadioGroupItem value="stripe" id="stripe" />
                      <CreditCard className="h-5 w-5 text-slate-600" />
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">Pay Now with Card</p>
                        <p className="text-sm text-slate-500">Secure payment via Stripe</p>
                      </div>
                    </label>
                  )}
                  {!isDelivery && payAtPickupEnabled && (
                    <label
                      className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                        formData.payment_method === "pay_at_pickup"
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid="payment-pickup"
                    >
                      <RadioGroupItem value="pay_at_pickup" id="pay_at_pickup" />
                      <Wallet className="h-5 w-5 text-slate-600" />
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">Pay at Pickup</p>
                        <p className="text-sm text-slate-500">Cash or card when you collect</p>
                      </div>
                    </label>
                  )}
                  {bankTransferEnabled && (
                    <label
                      className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                        formData.payment_method === "bank_transfer"
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid="payment-bank-transfer"
                    >
                      <RadioGroupItem value="bank_transfer" id="bank_transfer" />
                      <Landmark className="h-5 w-5 text-slate-600" />
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">Bank Transfer</p>
                        <p className="text-sm text-slate-500">Pay directly to our UK bank account</p>
                      </div>
                    </label>
                  )}
                </RadioGroup>

                {formData.payment_method === "bank_transfer" && (
                  <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="bank-details-box">
                    <p className="text-sm font-semibold text-amber-900 mb-3">Bank Transfer Details</p>
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                      <div className="flex justify-between sm:block">
                        <dt className="text-slate-500">Account Name</dt>
                        <dd className="font-medium text-slate-900">{bankAccountName}</dd>
                      </div>
                      <div className="flex justify-between sm:block">
                        <dt className="text-slate-500">Sort Code</dt>
                        <dd className="font-medium text-slate-900 tabular-nums">{bankSortCode}</dd>
                      </div>
                      <div className="flex justify-between sm:block">
                        <dt className="text-slate-500">Account Number</dt>
                        <dd className="font-medium text-slate-900 tabular-nums">{bankAccountNumber}</dd>
                      </div>
                      <div className="flex justify-between sm:block">
                        <dt className="text-slate-500">Reference</dt>
                        <dd className="font-medium text-slate-900">Your order number</dd>
                      </div>
                    </dl>
                    <p className="text-xs text-amber-800 mt-3">
                      Please transfer the full order total to the account above using your order number as the reference.
                      Your order will be confirmed once the transfer is received.
                    </p>
                  </div>
                )}
                {isDelivery && (
                  <p className="text-xs text-slate-500 mt-3" data-testid="delivery-payment-note">
                    Home delivery orders must be paid by card or bank transfer.
                  </p>
                )}
              </div>

              {/* Notes */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Order Notes (Optional)</h2>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => handleChange("notes", e.target.value)}
                  placeholder="Any special requests or notes for your order..."
                  rows={3}
                  data-testid="input-notes"
                />
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl p-6 shadow-sm sticky top-24">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Order Summary</h2>

                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {cart.map((item, index) => (
                    <div key={`${item.product_id}-${index}`} className="flex justify-between text-sm">
                      <span className="text-slate-600">
                        {item.quantity}x {item.product_name}
                        {item.weight_option && <span className="text-slate-400"> ({item.weight_option})</span>}
                      </span>
                      <span className="font-medium">£{(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>

                <Separator className="my-4" />

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Subtotal</span>
                    <span>£{cartTotal.toFixed(2)}</span>
                  </div>
                  {isDelivery ? (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Delivery</span>
                      <span data-testid="summary-delivery-fee">
                        {deliveryFee > 0 ? `£${deliveryFee.toFixed(2)}` : "—"}
                      </span>
                    </div>
                  ) : (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Collection</span>
                      <span className="text-emerald-600">Free</span>
                    </div>
                  )}
                </div>

                {isDelivery && minOrderShortfall > 0 && (
                  <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="min-order-warning">
                    Add <strong>£{minOrderShortfall.toFixed(2)}</strong> more to qualify for delivery (£{deliveryConfig.min_order} minimum).
                  </div>
                )}

                <Separator className="my-4" />

                <div className="flex justify-between text-lg font-semibold">
                  <span>Total</span>
                  <span data-testid="checkout-total">£{total.toFixed(2)}</span>
                </div>

                <Button
                  type="submit"
                  disabled={loading || cart.length === 0 || (isDelivery && minOrderShortfall > 0)}
                  className="w-full mt-6 h-12 bg-emerald-600 hover:bg-emerald-700 text-base font-medium rounded-xl"
                  data-testid="place-order-btn"
                >
                  {loading && "Processing..."}
                  {!loading && formData.payment_method === "stripe" && "Pay Now"}
                  {!loading && formData.payment_method === "bank_transfer" && "Place Order & Pay by Transfer"}
                  {!loading && formData.payment_method === "pay_at_pickup" && "Place Order"}
                </Button>

                <p className="text-xs text-slate-500 text-center mt-4">
                  By placing your order, you agree to {isDelivery ? "be available during" : "pickup during"} the selected time slot.
                </p>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
